import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Code2, ShieldCheck, Zap, GitBranch, Boxes, Database, Activity } from "lucide-react";
import Seo from "@/components/seo/Seo";
import { useLocalePath } from "@/i18n/LanguageSync";

export default function Apis() {
  const { t } = useTranslation("products");
  const lp = useLocalePath();
  return (
    <>
      <Seo
        title={t("apis.seoTitle")}
        description={t("apis.seoDescription")}
        path="/platform"
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 af-soft-glow pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 pb-24 md:pt-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <span
                data-testid="apis-eyebrow"
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-[#2563EB]"
              >
                <Code2 className="h-3.5 w-3.5" />
                {t("apis.eyebrow")}
              </span>
              <h1
                data-testid="apis-hero-title"
                className="font-display mt-6 text-[44px] sm:text-[56px] lg:text-[64px] leading-[1.02] text-[#0A1128]"
              >
                {t("apis.heroTitle")}
              </h1>
              <p className="mt-6 text-[17px] leading-relaxed text-slate-600 max-w-xl">
                {t("apis.heroBody")}
              </p>

              <div className="mt-9 flex flex-wrap gap-3">
                <Link
                  to={lp("/developers")}
                  data-testid="apis-cta-docs"
                  className="inline-flex items-center gap-2 rounded-full bg-[#0A1128] text-white text-[14px] font-medium px-5 py-3 hover:bg-[#1E293B] transition-colors"
                >
                  {t("apis.heroCtaSecondary")} <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link
                  to={lp("/status")}
                  data-testid="apis-cta-status"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white text-[#0A1128] text-[14px] font-medium px-5 py-3 hover:border-slate-400 transition-colors"
                >
                  <Activity className="h-4 w-4" /> Live API Status
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <pre className="af-code text-[12.5px]">
{`# create a new linked account
curl -X POST https://atlasforgefinancial.com/v1/accounts/link \\
  -H "Authorization: Bearer $ATLAS_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "usr_8M2nQ",
    "institution": "chase",
    "products": ["balances", "transactions", "auth"]
  }'

# response · 200 OK · 142 ms
{
  "id": "acc_a1B2c3D4",
  "institution": "chase",
  "type": "checking",
  "balance_available": 4218.50,
  "balance_current": 4302.10,
  "currency": "USD",
  "verified_at": "2026-06-14T19:21:00Z"
}`}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-[#F7F9FC] border-y border-slate-200/70">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="max-w-3xl">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Platform capabilities</p>
            <h2 className="font-display mt-3 text-[36px] md:text-[46px] leading-[1.05] text-[#0A1128]">
              Everything money needs, behind one platform.
            </h2>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Boxes, t: "Account connectivity", d: "Securely link checking, savings, credit, loan, and investment accounts." },
              { icon: Zap, t: "Money movement", d: "Send, receive, and reconcile payments across ACH, RTP, card, and wallet rails." },
              { icon: Database, t: "Transactions & balances", d: "Enriched, categorized transaction data and real-time balance APIs." },
              { icon: GitBranch, t: "Webhooks", d: "Reliable, signed event streams for every state change in the platform." },
              { icon: Activity, t: "Analytics & insights", d: "Cash flow forecasting, subscription detection, and AI categorization." },
              { icon: ShieldCheck, t: "Identity & KYC", d: "Customer onboarding workflows with built-in verification and risk signals." },
              { icon: Code2, t: "Developer experience", d: "Type-safe SDKs, idempotency, pagination, and predictable error semantics." },
              { icon: Boxes, t: "Sandboxes", d: "Realistic test data and zero-friction sandboxes for every endpoint." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="rounded-2xl bg-white border border-slate-200 p-6 af-shadow-card">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#EFF6FF]">
                  <Icon className="h-5 w-5 text-[#2563EB]" />
                </span>
                <h3 className="font-display mt-4 text-[17px]">{t}</h3>
                <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Endpoint preview */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">A glance at the surface area</p>
              <h2 className="font-display mt-3 text-[34px] md:text-[42px] leading-[1.05] text-[#0A1128]">
                Endpoints, built for clarity.
              </h2>
              <p className="mt-5 text-slate-600 max-w-md">
                RESTful, JSON-first, idempotent, and predictable. Designed by
                builders who&apos;ve shipped real fintech products.
              </p>
              <Link
                to="/developers"
                data-testid="apis-section-docs-link"
                className="mt-6 inline-flex items-center gap-2 text-[14px] font-medium text-[#0A1128] hover:gap-3 transition-all"
              >
                Read full reference <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="lg:col-span-7 rounded-2xl border border-slate-200 bg-white af-shadow-card divide-y divide-slate-100">
              {[
                ["GET", "/api/v1/me", "Partner profile, masked keys, tier, webhook status."],
                ["POST", "/api/v1/calculate/safe-to-spend", "Compute a user's daily allowance from cash, bills, savings, debt."],
                ["POST", "/api/v1/ingest", "Upsert + recompute a Safe to Spend 365 calculation for an end-user."],
                ["GET", "/api/v1/ingest/calculations", "Paginated list of every end-user's latest calculation."],
                ["GET", "/api/v1/usage", "Current-month calls, unique users, estimated bill (sandbox + production)."],
                ["POST", "/api/v1/webhooks", "Register a webhook URL for `calculation.updated` events."],
                ["DELETE", "/api/v1/ingest/user/{id}", "GDPR delete — wipe one end-user's cached calculation."],
              ].map(([m, path, d]) => (
                <div key={path} className="flex items-start gap-4 p-5">
                  <span className={`text-[11px] font-mono font-semibold rounded px-2 py-0.5 ${m === "POST" ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700"}`}>
                    {m}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[13.5px] text-[#0A1128]">{path}</p>
                    <p className="text-[13px] text-slate-600 mt-0.5">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A1128] text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <h2 className="font-display text-[36px] md:text-[46px] leading-[1.05]">
              Build embedded finance, faster.
            </h2>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Link
                to="/developers"
                data-testid="apis-bottom-cta-docs"
                className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A1128] text-[14px] font-medium px-5 py-3 hover:bg-slate-100 transition-colors"
              >
                Developer docs <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="apis-bottom-cta-contact"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white text-[14px] font-medium px-5 py-3 hover:bg-white/5 transition-colors"
              >
                Contact platform team
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
